import React, { useMemo, useState } from "react";
import Head from "../../../../layout/head/Head";
import Content from "../../../../layout/content/Content";
import TablePageHeading from "../page_heading/TablePageHeading";
import { Col, Row, Button } from "../../../../components/Component";
import { Spinner } from "reactstrap";
import { motion } from "framer-motion";
import {
  BiPlus,
  BiTrash,
  BiReceipt,
  BiCalculator,
  BiUser,
  BiBriefcase,
} from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import TextInput from "../../../components/inputs/TextInput";
import * as app_consts from "../../../../constants/react_app_consts";
import { messageToast } from "../../../../utils/Utils";
import {
  createSmartInvoice,
  generateSmartInvoiceNumber,
  getSupportedInvoiceCurrencies,
} from "../../../../services/smart_invoice_service";

const type = "SMART_INVOICE_CREATE";

const toNum = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatMoney = (value) =>
  toNum(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getDefaultLineItem = () => ({
  id: Date.now() + Math.random(),
  commodity: "",
  description: "",
  tonnage: "",
  price_per_mt: "",
  cost_per_mt: "",
  transport_ex_sa: "",
  transport_local: "",
  import_costs: "",
  commission: "",
});

const SmartInvoiceCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState({
    invoice_number: generateSmartInvoiceNumber(),
    invoice_date: new Date().toISOString().split("T")[0],
    customer_name: "",
    customer_tpin: "",
    customer_address: "",
    delivery_note_ref: "",
    re_reference: "",
    currency: "ZMW",
    exchange_rate: 1,
    payment_terms: "Payment due within agreed terms.",
    bank_details: "Bank details to be provided.",
    notes: "",
  });
  const [invoiceLines, setInvoiceLines] = useState([getDefaultLineItem()]);

  const supportedCurrencies = useMemo(() => getSupportedInvoiceCurrencies(), []);

  const cardMotion = useMemo(
    () => ({
      hidden: { opacity: 0, y: 12, scale: 0.985 },
      show: (i = 0) => ({
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.35, delay: i * 0.08, ease: "easeOut" },
      }),
    }),
    []
  );

  const hoverMotion = useMemo(
    () => ({
      whileHover: { y: -4, transition: { duration: 0.18, ease: "easeOut" } },
      whileTap: { scale: 0.99 },
    }),
    []
  );

  const computedLines = useMemo(() => {
    return invoiceLines.map((line) => {
      const tonnage = toNum(line.tonnage);
      const pricePerMt = toNum(line.price_per_mt);
      const lineTotalForeign = tonnage * pricePerMt;
      const exchangeRate = invoiceData.currency === "ZMW" ? 1 : Math.max(toNum(invoiceData.exchange_rate), 1);
      const lineTotal = invoiceData.currency === "ZMW" ? lineTotalForeign : lineTotalForeign * exchangeRate;
      const totalCostPerMt =
        toNum(line.cost_per_mt) +
        toNum(line.transport_ex_sa) +
        toNum(line.transport_local) +
        toNum(line.import_costs) +
        toNum(line.commission);
      const totalCost = tonnage * totalCostPerMt;
      const grossProfit = lineTotal - totalCost;
      const marginPct = lineTotal > 0 ? (grossProfit / lineTotal) * 100 : 0;

      return {
        ...line,
        tonnage,
        price_per_mt: pricePerMt,
        line_total_foreign: lineTotalForeign,
        line_total: lineTotal,
        total_cost_per_mt: totalCostPerMt,
        total_cost: totalCost,
        gross_profit: grossProfit,
        margin_pct: marginPct,
      };
    });
  }, [invoiceLines, invoiceData.currency, invoiceData.exchange_rate]);

  const subtotal = useMemo(() => computedLines.reduce((acc, curr) => acc + toNum(curr.line_total), 0), [computedLines]);
  const vatAmount = useMemo(() => subtotal * 0.16, [subtotal]);
  const totalDue = useMemo(() => subtotal + vatAmount, [subtotal, vatAmount]);
  const totalGrossProfit = useMemo(() => computedLines.reduce((acc, curr) => acc + toNum(curr.gross_profit), 0), [computedLines]);

  const handleInvoiceFieldChange = (field, value) => {
    setInvoiceData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleLineFieldChange = (id, field, value) => {
    setInvoiceLines((prev) =>
      prev.map((line) => (line.id === id ? { ...line, [field]: value } : line))
    );
  };

  const addLineItem = () => setInvoiceLines((prev) => [...prev, getDefaultLineItem()]);

  const removeLineItem = (id) => {
    if (invoiceLines.length === 1) {
      messageToast("At least one invoice line is required.", app_consts.FAIL);
      return;
    }

    setInvoiceLines((prev) => prev.filter((line) => line.id !== id));
  };

  const validateForm = () => {
    if (!invoiceData.customer_name.trim()) {
      messageToast("Customer name is required.", app_consts.FAIL);
      return false;
    }

    if (!invoiceData.invoice_date) {
      messageToast("Invoice date is required.", app_consts.FAIL);
      return false;
    }

    if (!invoiceData.delivery_note_ref.trim()) {
      messageToast("Delivery note reference is required.", app_consts.FAIL);
      return false;
    }

    if (invoiceData.currency !== "ZMW" && toNum(invoiceData.exchange_rate) <= 0) {
      messageToast("A valid exchange rate is required for non-ZMW invoices.", app_consts.FAIL);
      return false;
    }

    const hasValidLine = computedLines.some(
      (line) => line.commodity?.trim() && toNum(line.tonnage) > 0 && toNum(line.price_per_mt) > 0
    );

    if (!hasValidLine) {
      messageToast("Please add at least one valid invoice line.", app_consts.FAIL);
      return false;
    }

    return true;
  };

  const buildInvoicePayload = () => ({
    ...invoiceData,
    exchange_rate: invoiceData.currency === "ZMW" ? 1 : toNum(invoiceData.exchange_rate),
    invoice_lines: computedLines.map((line) => ({
      id: line.id,
      commodity: line.commodity,
      description: line.description,
      tonnage: line.tonnage,
      price_per_mt: line.price_per_mt,
      cost_per_mt: toNum(line.cost_per_mt),
      transport_ex_sa: toNum(line.transport_ex_sa),
      transport_local: toNum(line.transport_local),
      import_costs: toNum(line.import_costs),
      commission: toNum(line.commission),
    })),
  });

  const handleSaveDraft = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      const createdInvoice = await createSmartInvoice(buildInvoicePayload(), { submit: false });
      messageToast("Smart Invoice draft saved successfully.", app_consts.SUCCESS);
      navigate(`/dashboard/smart_invoices/${createdInvoice.id}`);
    } catch (err) {
      messageToast("Failed to save Smart Invoice draft.", app_consts.FAIL);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitToZRA = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      const createdInvoice = await createSmartInvoice(buildInvoicePayload(), { submit: true });
      messageToast("Smart Invoice submitted successfully.", app_consts.SUCCESS);
      navigate(`/dashboard/smart_invoices/${createdInvoice.id}`);
    } catch (err) {
      messageToast("Failed to submit Smart Invoice.", app_consts.FAIL);
    } finally {
      setLoading(false);
    }
  };

  const IconCard = ({ icon: IconComp, title, amount }) => (
    <div className="card card-bordered h-100">
      <div className="card-inner">
        <div className="d-flex align-items-center justify-content-between mb-1">
          <div className="text-soft">{title}</div>
          <IconComp className="fs-4 text-primary" />
        </div>
        <div className="fs-22px fw-bold">{amount}</div>
      </div>
    </div>
  );

  return (
    <>
      <Head title="Create Smart Invoice" />
      <Content>
        <TablePageHeading title="Create Smart Invoice" page_type={type} pageData={computedLines} />

        {loading ? (
          <div className="card card-bordered">
            <div className="card-inner py-5">
              <div className="d-flex flex-column align-center justify-center">
                <Spinner />
                <div className="fs-15px mt-2 text-soft">Processing Smart Invoice...</div>
              </div>
            </div>
          </div>
        ) : (
          <Row className="g-gs">
            <Col xs="12">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
                <Row className="g-gs">
                  <Col xxl="3" sm="6">
                    <motion.div className="h-100" variants={cardMotion} initial="hidden" animate="show" custom={0} {...hoverMotion}>
                      <IconCard icon={BiReceipt} title="Subtotal" amount={`ZMW ${formatMoney(subtotal)}`} />
                    </motion.div>
                  </Col>
                  <Col xxl="3" sm="6">
                    <motion.div className="h-100" variants={cardMotion} initial="hidden" animate="show" custom={1} {...hoverMotion}>
                      <IconCard icon={BiCalculator} title="VAT (16%)" amount={`ZMW ${formatMoney(vatAmount)}`} />
                    </motion.div>
                  </Col>
                  <Col xxl="3" sm="6">
                    <motion.div className="h-100" variants={cardMotion} initial="hidden" animate="show" custom={2} {...hoverMotion}>
                      <IconCard icon={BiBriefcase} title="Total Due" amount={`ZMW ${formatMoney(totalDue)}`} />
                    </motion.div>
                  </Col>
                  <Col xxl="3" sm="6">
                    <motion.div className="h-100" variants={cardMotion} initial="hidden" animate="show" custom={3} {...hoverMotion}>
                      <IconCard icon={BiUser} title="Gross Profit" amount={`ZMW ${formatMoney(totalGrossProfit)}`} />
                    </motion.div>
                  </Col>
                </Row>
              </motion.div>
            </Col>

            <Col xxl="8">
              <div className="card card-bordered">
                <div className="card-inner border-bottom">
                  <div className="fs-15px fw-bold">Customer-Facing Invoice Section</div>
                  <div className="text-soft small">This section will appear on the final PDF sent to the customer.</div>
                </div>

                <div className="card-inner">
                  <Row className="g-3">
                    <TextInput label="Invoice Number" name="invoice_number" id_val="invoice_number" value={invoiceData.invoice_number} handleChange={(e) => handleInvoiceFieldChange("invoice_number", e.target.value)} colSize="6" />
                    <TextInput label="Invoice Date" name="invoice_date" id_val="invoice_date" type="date" value={invoiceData.invoice_date} handleChange={(e) => handleInvoiceFieldChange("invoice_date", e.target.value)} colSize="6" />
                    <TextInput label="Customer Name" name="customer_name" id_val="customer_name" value={invoiceData.customer_name} handleChange={(e) => handleInvoiceFieldChange("customer_name", e.target.value)} colSize="6" />
                    <TextInput label="Customer TPIN" name="customer_tpin" id_val="customer_tpin" value={invoiceData.customer_tpin} handleChange={(e) => handleInvoiceFieldChange("customer_tpin", e.target.value)} colSize="6" />
                    <TextInput label="Customer Address" name="customer_address" id_val="customer_address" value={invoiceData.customer_address} handleChange={(e) => handleInvoiceFieldChange("customer_address", e.target.value)} colSize="12" />
                    <TextInput label="Delivery Note Ref" name="delivery_note_ref" id_val="delivery_note_ref" value={invoiceData.delivery_note_ref} handleChange={(e) => handleInvoiceFieldChange("delivery_note_ref", e.target.value)} colSize="6" />
                    <TextInput label="RE / Reference" name="re_reference" id_val="re_reference" value={invoiceData.re_reference} handleChange={(e) => handleInvoiceFieldChange("re_reference", e.target.value)} colSize="6" />

                    <Col md="6">
                      <div className="form-group">
                        <div className="form-label-group">
                          <label className="form-label" htmlFor="currency">Currency</label>
                        </div>
                        <div className="form-control-wrap">
                          <select
                            id="currency"
                            className="form-select"
                            value={invoiceData.currency}
                            onChange={(e) => handleInvoiceFieldChange("currency", e.target.value)}
                          >
                            {supportedCurrencies.map((currency) => (
                              <option key={currency.code} value={currency.code}>
                                {currency.code} - {currency.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </Col>

                    <TextInput
                      label="Exchange Rate"
                      name="exchange_rate"
                      id_val="exchange_rate"
                      value={invoiceData.exchange_rate}
                      handleChange={(e) => handleInvoiceFieldChange("exchange_rate", e.target.value)}
                      colSize="6"
                      disabled={invoiceData.currency === "ZMW"}
                    />
                  </Row>
                </div>
              </div>

              <div className="card card-bordered mt-4">
                <div className="card-inner border-bottom d-flex justify-content-between align-items-center">
                  <div>
                    <div className="fs-15px fw-bold">Invoice Line Items</div>
                    <div className="text-soft small">Add items that will appear on the customer invoice.</div>
                  </div>

                  <Button color="primary" size="sm" onClick={addLineItem}>
                    <BiPlus className="me-1" />
                    Add Line
                  </Button>
                </div>

                <div className="card-inner">
                  {computedLines.map((line, index) => (
                    <div key={line.id} className="border rounded p-3 mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="fw-bold">Line Item #{index + 1}</div>

                        <button type="button" className="btn btn-sm btn-danger" onClick={() => removeLineItem(line.id)}>
                          <BiTrash className="me-1" />
                          Remove
                        </button>
                      </div>

                      <Row className="g-3">
                        <TextInput label="Commodity" name={`commodity_${line.id}`} id_val={`commodity_${line.id}`} value={line.commodity} handleChange={(e) => handleLineFieldChange(line.id, "commodity", e.target.value)} colSize="4" />
                        <TextInput label="Description" name={`description_${line.id}`} id_val={`description_${line.id}`} value={line.description} handleChange={(e) => handleLineFieldChange(line.id, "description", e.target.value)} colSize="4" />
                        <TextInput label="Tonnage (MT)" name={`tonnage_${line.id}`} id_val={`tonnage_${line.id}`} value={line.tonnage} handleChange={(e) => handleLineFieldChange(line.id, "tonnage", e.target.value)} colSize="2" />
                        <TextInput label={`Price / MT (${invoiceData.currency})`} name={`price_per_mt_${line.id}`} id_val={`price_per_mt_${line.id}`} value={line.price_per_mt} handleChange={(e) => handleLineFieldChange(line.id, "price_per_mt", e.target.value)} colSize="2" />

                        <Col md="12">
                          <div className="alert alert-primary mt-2 mb-0">
                            <strong>Line Total:</strong> ZMW {formatMoney(line.line_total)}
                          </div>
                        </Col>
                      </Row>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card card-bordered mt-4">
                <div className="card-inner border-bottom">
                  <div className="fs-15px fw-bold">Payment & Additional Information</div>
                </div>

                <div className="card-inner">
                  <Row className="g-3">
                    <TextInput label="Payment Terms" name="payment_terms" id_val="payment_terms" value={invoiceData.payment_terms} handleChange={(e) => handleInvoiceFieldChange("payment_terms", e.target.value)} colSize="12" />
                    <TextInput label="Bank Details" name="bank_details" id_val="bank_details" value={invoiceData.bank_details} handleChange={(e) => handleInvoiceFieldChange("bank_details", e.target.value)} colSize="12" />
                    <TextInput label="Notes" name="notes" id_val="notes" value={invoiceData.notes} handleChange={(e) => handleInvoiceFieldChange("notes", e.target.value)} colSize="12" />
                  </Row>
                </div>
              </div>
            </Col>

            <Col xxl="4">
              <div className="card card-bordered">
                <div className="card-inner border-bottom">
                  <div className="fs-15px fw-bold">Internal Company Section</div>
                  <div className="text-soft small">For internal costing and profit analysis only. Not included in PDF.</div>
                </div>

                <div className="card-inner">
                  {computedLines.map((line, index) => (
                    <div key={`internal-${line.id}`} className="border rounded p-3 mb-3">
                      <div className="fw-bold mb-3">Internal Costing #{index + 1}</div>

                      <Row className="g-2">
                        <TextInput label="Cost / MT" name={`cost_per_mt_${line.id}`} id_val={`cost_per_mt_${line.id}`} value={line.cost_per_mt} handleChange={(e) => handleLineFieldChange(line.id, "cost_per_mt", e.target.value)} colSize="12" />
                        <TextInput label="Transport Ex SA" name={`transport_ex_sa_${line.id}`} id_val={`transport_ex_sa_${line.id}`} value={line.transport_ex_sa} handleChange={(e) => handleLineFieldChange(line.id, "transport_ex_sa", e.target.value)} colSize="12" />
                        <TextInput label="Transport Local" name={`transport_local_${line.id}`} id_val={`transport_local_${line.id}`} value={line.transport_local} handleChange={(e) => handleLineFieldChange(line.id, "transport_local", e.target.value)} colSize="12" />
                        <TextInput label="Import Costs" name={`import_costs_${line.id}`} id_val={`import_costs_${line.id}`} value={line.import_costs} handleChange={(e) => handleLineFieldChange(line.id, "import_costs", e.target.value)} colSize="12" />
                        <TextInput label="Commission" name={`commission_${line.id}`} id_val={`commission_${line.id}`} value={line.commission} handleChange={(e) => handleLineFieldChange(line.id, "commission", e.target.value)} colSize="12" />
                      </Row>

                      <hr />

                      <div className="d-flex justify-content-between py-1">
                        <span className="text-soft">Total Cost / MT</span>
                        <span className="fw-bold">ZMW {formatMoney(line.total_cost_per_mt)}</span>
                      </div>

                      <div className="d-flex justify-content-between py-1">
                        <span className="text-soft">Total Cost</span>
                        <span className="fw-bold">ZMW {formatMoney(line.total_cost)}</span>
                      </div>

                      <div className="d-flex justify-content-between py-1">
                        <span className="text-soft">Gross Profit</span>
                        <span className="fw-bold">ZMW {formatMoney(line.gross_profit)}</span>
                      </div>

                      <div className="d-flex justify-content-between py-1">
                        <span className="text-soft">Margin %</span>
                        <span className="fw-bold">{formatMoney(line.margin_pct)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card card-bordered mt-4">
                <div className="card-inner border-bottom">
                  <div className="fs-15px fw-bold">Totals Summary</div>
                </div>

                <div className="card-inner">
                  <div className="d-flex justify-content-between py-2">
                    <span className="text-soft">Subtotal</span>
                    <span className="fw-bold">ZMW {formatMoney(subtotal)}</span>
                  </div>

                  <div className="d-flex justify-content-between py-2">
                    <span className="text-soft">VAT (16%)</span>
                    <span className="fw-bold">ZMW {formatMoney(vatAmount)}</span>
                  </div>

                  <div className="d-flex justify-content-between py-2">
                    <span className="text-soft">Total Due</span>
                    <span className="fw-bold">ZMW {formatMoney(totalDue)}</span>
                  </div>

                  <div className="d-flex justify-content-between py-2">
                    <span className="text-soft">Gross Profit</span>
                    <span className="fw-bold">ZMW {formatMoney(totalGrossProfit)}</span>
                  </div>
                </div>
              </div>

              <div className="card card-bordered mt-4">
                <div className="card-inner">
                  <div className="d-grid gap-2">
                    <Button color="light" onClick={handleSaveDraft}>
                      Save Draft
                    </Button>

                    <Button color="primary" onClick={handleSubmitToZRA}>
                      Submit to ZRA
                    </Button>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        )}
      </Content>
    </>
  );
};

export default SmartInvoiceCreate;
